import { FormattedMessage } from 'react-intl';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { globalTheme, useMixin } from '@clds/component-theme';
import { Chip, ChipsList } from '@clds/chip';
import { Icon } from '@cld/icon';
import { theme } from '../CustomTransformationCardTheme';
import { SearchOption } from '../../../SearchField/SearchField.helper';
import { messages } from '../../../../i18n';
import { StrictTransformationsStatus } from '../../../../types';

export interface CustomTransformationChipsProps {
  children?: ReactNode;
  isAllowedForStrictTransformations: boolean;
  isInPresets: boolean;
  strictTransformationsStatus: StrictTransformationsStatus;
  onChipClick?: (option: SearchOption) => void;
}

export const ChipsWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  > * {
    margin-top: ${globalTheme.spacing.xxs};
    margin-bottom: ${globalTheme.spacing.xxs};
  }
`;

const useColorIndicator = (strictTransformationsStatus: StrictTransformationsStatus, isAllowedForStrictTransformations: boolean) => {
  const enabledColor = isAllowedForStrictTransformations ? theme.successColor : theme.dangerColor;
  const disabledColor = theme.disabledColor;
  return useMixin(strictTransformationsStatus === 'enabled' ? enabledColor : disabledColor);
};

export const CustomTransformationChips: FC<React.PropsWithChildren<CustomTransformationChipsProps>> = ({
  children,
  isAllowedForStrictTransformations,
  isInPresets,
  strictTransformationsStatus,
  onChipClick,
}) => {
  const color = useColorIndicator(strictTransformationsStatus, isAllowedForStrictTransformations);
  return (
    <ChipsList data-test-specifier="custom-transformation-chips">
      <ChipsWrapper>
        {children}
        <Chip
          isHoverable={!!onChipClick}
          onClick={() => !!onChipClick && onChipClick({ isAllowedForStrictTransformations })}
          leftSlot={<Icon name="circle" size="xs" color={color} />}
          label={
            <FormattedMessage
              {...(isAllowedForStrictTransformations ? messages.allowedInStrictTransformations : messages.notAllowedInStrictTransformations)}
            />
          }
        />
        {isInPresets && (
          <Chip
            isHoverable={!!onChipClick}
            onClick={() => !!onChipClick && onChipClick({ isInPresets: true })}
            label={<FormattedMessage {...messages.inPresets} />}
          />
        )}
      </ChipsWrapper>
    </ChipsList>
  );
};
